import {
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import {
  debounce,
  filter,
  find,
  forEach,
  get,
  isEmpty,
  map,
  merge,
  remove,
  round,
  some,
  uniqueId,
  first
} from 'lodash';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import {
  DateFormatterService,
  FormatService,
  KPIDetails,
  KpiDetailsService,
  TimeoutWrapperService,
  TimePickerService,
  TimeRange,
  TooltipService,
  UNIT
} from '@nrwl/common-lib';
import * as Highcharts from 'highcharts';

type ChartType = 'line' | 'area' | 'spline' | 'areaspline'; //chart.defaultSeriesType
type AlignType = 'left' | 'center' | 'right';

interface ITextType {
  visible?: boolean;
  align?: AlignType;
  text?: string;
}

interface IAxis {
  visible: boolean;
  grid: boolean;

  title?: {
    text?: string | null;
  };
  labels?: {
    format?: string; //"{value} b/s"
  };
  type?: 'linear' | 'logarithmic' | 'datetime' | 'category';
}

interface IXAxis extends IAxis {
  boundary: number;
}

export interface IChartOptions {
  type?: ChartType;
  title?: ITextType;
  subtitle?: ITextType;
  xAxis?: IXAxis;
  yAxis?: IAxis;
  series?: Array<Highcharts.SeriesOptionsType>;
  override?: Highcharts.Options;
  useNamedSerie?: boolean;
  showPinning?: boolean;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  crosshairMargin?: any;
  maxPinCount?: number;
  isPinApplicable?: boolean;
  showTooltipTotal?: boolean;
  seriesColor?: string;
  additionalTooltip?: Array<{ label: string; value: string }>;
}

const blueColor = 'rgb(233, 233, 235)';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const conversionFactors = {
  ns: 1,
  '%C2%B5s': 1000, //note the different UTF character, has to be same as server response
  ms: 1000000,
  s: 1000000000,
  min: 60 * 1000000000,
  h: 3600 * 1000000000,
  d: 24 * 3600 * 1000000000
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const unitAliases = {
  seconds: 's',
  milliseconds: 'ms',
  microseconds: '%C2%B5s',
  nanoseconds: 'ns'
};

@Component({
  selector: 'nrwl-single-value-chart',
  templateUrl: './single.value.chart.template.html',
  styleUrls: ['./single.value.chart.style.scss'],
  providers: [TimeoutWrapperService]
})
export class SingleValueChartComponent implements OnInit, OnDestroy, OnChanges {
  @Input() options: IChartOptions;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() dataSource: any; //First query/after filter change response
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() deltaDataSource: any; //Live data
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() events: Subject<{ key: string; props?: any }>;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() loader: any;
  @Input() loaderMessage: string;
  // looks like initially this input was assumed to pass over a human readable label
  // however, this role was deprecated/I don't see any use of this prop.
  @Input() yAxisUnitLabel: string;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() notifyPinEventListeners: any;
  // used to get granularity value for data to show the endtime in tooltip
  @Input() granularity?: number;

  @Input() kpiUnit?: string = 'milliseconds';
  @Input() chartZoomWithTime = true;

  // works in combination with @input conversionUnits
  // the conversionUnits supply text-labels, the formatter, indicated by yAxisTickLabelFormat,
  // will take care to supply numeric value to be shown with that label.
  // Supported cases:
  // a) 'numeric-thousands' Y-axis displays just numbers, you may decide to show them in 'thousands'
  //    (eg, 1, 1000, 1,000,000, 1,000,000,000, etc). Anything that is less than 1000, will be shown as 0
  // b) 'numeric' Very similar to numeric-thousands. The only difference: if value is less than 1000, it is still
  //    displayed/ie, it is not suppressed to 0.
  // c) 'time' - Y axis will display time (ns, ms, sec, min, hr, day, month, year, ...). The special
  //    logic here is that from certain value, it switches from 10-based to 60-based calculation.
  // the code defaults to 'numeric-thousands', if input not given.
  @Input() yAxisTickLabelFormat: 'numeric-thousands' | 'time' | 'numeric' | 'fixed' | 'percentage';
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() conversionUnits: any;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() chartControls: any;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() notifyZoomEventListeners: any;
  @Input() kpiName?: string;
  //eslint-disable-next-line
  @Input() removePinEvent?: BehaviorSubject<any>;
  id: string;
  chart: Highcharts.Chart;
  isLoading = false;
  eventsSubscription: Subscription;
  mainColor: string;
  secColor: string;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  pinnedArr: any;
  maxPinCount: number;
  classes: Array<string> = [];
  pinsSubscriber: Subscription;
  pinSubscription: Subscription;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  boundedWindowResizeListener: any;

  //Increasing to 3000 per OLPTC-4511
  turboThreshold = 3000;
  minTickInterval = 60 * 1000;
  zoomTickInterval = 3 * 60 * 1000;
  isChartZoomed = false;
  datetimeExtremes: { min: number; max: number };
  graphZoomed = 'false';
  // as redraw of charts is being done with timeout, it may happen that component instance
  // is destroyed before timeout fires. This is a way to check the widget is gone..
  private onWayOut = false;

  constructor(
    private readonly timeout: TimeoutWrapperService,
    private readonly dateFormatterService: DateFormatterService,
    private readonly tooltipService: TooltipService,
    private readonly kpiDetailsService: KpiDetailsService,
    private readonly formatService: FormatService,
    private readonly timePicker: TimePickerService
  ) {
    this.id = uniqueId('single-value-chart-');

    this.boundedWindowResizeListener = debounce(() => {
      this.onResizeEvent();
    }, 400);
  }

  @HostListener('window:resize')
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onResize(event): void {
    this.boundedWindowResizeListener(event);
  }

  ngOnInit() {
    this.pinnedArr = [];
    // some controls may reduce max pin count - eg single pin controls in VOIP
    this.maxPinCount = 2;

    if (this.chartControls) {
      this.chartControls.widgets.push(this);
    }
    this.datetimeExtremes = {
      min: 0,
      max: 0
    };
    this.pinSubscription = this.removePinEvent?.subscribe(pin => {
      if (pin) this.onPinnedIconClicked(pin);
    });
    if (this.events) {
      // looks like our loading will be controlled via isLoading. Make sure we start with loader active
      this.isLoading = true;
      this.eventsSubscription = this.events.subscribe(eventName => {
        switch (eventName.key) {
          case 'onResize':
            this.onResizeEvent();
            break;
          case 'onLoad':
            this.isLoading = true;
            break;
          case 'afterLoad':
            // this won't be fired anymore by sankey
            this.isLoading = false;
            this.redraw();
            break;
          default:
        }
      });
    } else {
      this.redraw();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // //Redraw chart after new dataSource is set
    // if (changes.dataSource) {
    //   if (!changes.dataSource.firstChange && changes.dataSource.currentValue !== changes.dataSource.previousValue) {
    //     this.redraw();
    //   }
    // }
    //
    if (
      get(changes.deltaDataSource, 'currentValue') &&
      changes.deltaDataSource.currentValue !== changes.deltaDataSource.previousValue
    ) {
      this.onUpdate(this.deltaDataSource);
    }

    // note: need to test whether redraw() logic is friendly to 'zoom' state (eg, if chart was zoomed to show small range, it would be
    // nice if chart redraw respects that)
    if (changes.dataSource) {
      // an important remark:
      // - it might be the response from backend has arrived sooner than angular has finished initializing
      // and loading the component. Hence, the very firstChange will contain data to show - and there will be
      // NO more notifications! Therefore our check for data to show should not consider if it is 'firstChange'.
      // you've got data - then make it visible.
      if (changes.dataSource.currentValue && changes.dataSource.currentValue.length > 0) {
        this.isLoading = false;
        this._updateChart();
      } else {
        // data is not there. But in case this is not 'firstChange', we want to hide loader and show there
        // is no data
        if (!changes.dataSource.firstChange) {
          this.isLoading = false;
          this._updateChart();
        }
      }
    }

    // if (changes.options && !changes.options.firstChange && changes.options.currentValue !== changes.options.previousValue) {
    //   this.chart.update(this.getChartOptions());
    // }
  }

  ngOnDestroy() {
    this.onWayOut = true;
    if (this.pinsSubscriber) {
      this.pinsSubscriber.unsubscribe();
    }
    if (this.pinSubscription) this.pinSubscription.unsubscribe();
    if (!isEmpty(this.chart)) {
      this.chart.destroy();
    }
    if (this.eventsSubscription) {
      this.eventsSubscription.unsubscribe();
    }
    if (this.chartControls) {
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      remove(this.chartControls.widgets, this as any);
    }
    if (this.timeout) {
      // we can do that since timeout service is privately provided to this widget
      this.timeout.onDestroy();
    }
  }

  onResizeEvent(): void {
    if (!isEmpty(this.chart)) {
      this.chart.destroy();
    }
    this.timeout.add(() => this.draw(), 0);
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  get loading(): any {
    return this.loader ? this.isLoading || this.loaderMessage : false;
  }

  _updateChart(): void {
    if (this.onWayOut) {
      return;
    }
    if (this.chart?.series && this.dataSource?.length) {
      //eslint-disable-next-line
      const chartOpt: any = this.getChartOptions();
      if (chartOpt && chartOpt.series && chartOpt.series.length > 0) {
        //Update min/max values (remove gaps between data and thresholds)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const xAxis: any = first(this.chart.xAxis);
        if (xAxis) {
          xAxis.setOptions(chartOpt.xAxis);
        }

        for (let i = 0, len = chartOpt.series.length; i < len; ++i) {
          if (this.chart.series[i]) {
            this.chart.series[i].setData(chartOpt.series[i].data);
          } else {
            this.chart.addSeries(chartOpt.series[i], false);
          }
        }
        this.chart.update(this.getChartOptions());
        this.chart.redraw();
      }
    } else {
      this.redraw();
    }
  }

  draw(): void {
    this.graphZoomed = sessionStorage.getItem('graphZoomed');
    const lastTimeRange: TimeRange = this.timePicker.lastTimeRange;
    if (this.onWayOut) {
      return;
    }
    if (!this.loading) {
      this.mainColor = this.options.seriesColor
        ? this.options.seriesColor
        : Highcharts.getOptions().colors[0];
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.secColor = (Highcharts as any).Color(this.mainColor).setOpacity(0).get('rgba');
      this.chart = Highcharts.chart(
        this.id,
        merge(this.getChartOptions(), get(this.options, 'chart', {}))
      );
      if (this.graphZoomed === 'true') {
        this.chart.renderer
          .button('Reset Zoom', this.chart.chartWidth - 100, 40, null)
          .attr({ zIndex: 3 })
          .on('click', () => {
            this.timePicker.replaceTimeRangeOnChartZoom(lastTimeRange, true, false);
          })
          .add();
      }
      if (this.pinnedArr.length > 0 && get(this.options, 'keepPins', false)) {
        const pinnedArr = filter(
          this.pinnedArr,
          pin =>
            pin.value >= get(this.chart.xAxis[0], 'dataMin') &&
            pin.value <= get(this.chart.xAxis[0], 'dataMax')
        );
        this.pinnedArr = [];
        forEach(pinnedArr, pin => {
          const yVal = find(this.dataSource[0].data, o => o.x === pin.value).y;
          this.showPinnedValue(pin.value, null, yVal);
        });
      } else {
        this.pinnedArr = [];
      }
    }
  }

  redraw(): void {
    if (!isEmpty(this.chart)) {
      this.chart.destroy();
    }
    setTimeout(() => {
      this.draw();
    }, 1000);
  }

  // implements numeric-thousands label formatter
  bitsToSize(bits: number): string {
    //const sizes = ['0', 'Kb/s', 'Mb/s', 'Gb/s', 'Tb/s'];
    const i = Math.floor(Math.log(bits) / Math.log(1000));
    if (i <= 0) return `${Math.round(bits)} ${this.conversionUnits[0]}`;
    return round(bits / Math.pow(1000, i), 2) + ' ' + this.conversionUnits[0];
  }

  fixedY_axixLabel(sec: number): string {
    return `${round(sec, 2)} ${this.conversionUnits[0]}`;
  }

  showPinnedValue(xVal: number, seriesId: string, yVal: number): void {
    if (
      this.pinnedArr.length < this.maxPinCount &&
      !some(this.pinnedArr, pin => pin.value === xVal)
    ) {
      const pinDetails = this.addPin(xVal);
      if (pinDetails) {
        this.showSelectedValue(pinDetails, xVal, seriesId, yVal);
      }
    } else {
      if (!this._allowMorePins()) {
        return;
      }
      this.timeout.set(
        'repinLast',
        () => {
          const lastPinName = this.pinnedArr[this.pinnedArr.length - 1].id;
          // these 2 steps handle pin visualisation locally. No events emited
          const oldPinDetails = this.removeSelectedPin(lastPinName);
          const pinDetails = this.addPin(xVal);

          if (pinDetails) {
            const event = {
              state: 'replace',
              fromTime: xVal,
              toTime: xVal,
              oldPinDetails: oldPinDetails,
              pinDetails: pinDetails,
              yVal: yVal,
              originator: this.id
            };
            this.notifyPinEventListeners(event); //{event: event}
          }
        },
        0
      );
    }
  }

  /*-Inserts pin to current control/chart;
    - if total pin count on the chart reached the limit (1-2 pins, depending on config), disables further 'pin' functionality
    - NO events emitted to outside
  */

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  addPin(pointX: number): any {
    if (this.pinnedArr.length < this.maxPinCount) {
      const pinId = this._buildPin(pointX);

      return { pinNumber: this.pinnedArr.length, pinId: pinId };
    } else {
      // ignore requests to do so
    }
    return null;
  }

  _allowMorePins(): boolean {
    if (this.pinnedArr.length >= this.maxPinCount) {
      // for single-pinned controls allow replacing that pin with another one
      // eg, voip details page. For two-pinned controls (eg traceroute), keep this
      // feature disabled/as I didn't get request to enable such support.
      if (this.maxPinCount === 2) {
        return false;
      }
    }
    return true;
  }

  _buildPin(pointX: number): string {
    const plTemplate = {
      id: 'pin',
      color: '#4299f5',
      width: 2,
      zIndex: 50,
      value: null,
      className: 'pin-event-1',
      label: {
        useHTML: true,
        text: '',
        verticalAlign: 'top',
        rotation: 0,
        x: -12,
        y: 0,
        zIndex: 100
      }
    };
    this.pinnedArr.push(JSON.parse(JSON.stringify(plTemplate)));
    const pinId = 'pin-' + this.pinnedArr.length;
    const idx = this.pinnedArr.length - 1;
    let pinSrc = './images/pin-1.svg';
    if (idx === 1) {
      this.pinnedArr[idx].color = '#fa7921';
      this.pinnedArr[idx].className = 'pin-event-2';
      pinSrc = './images/pin-2.svg';
    }

    this.pinnedArr[
      idx
    ].label.text = `<img id='${pinId}' class='${pinId}' src='${pinSrc}' width='24' height='24' />`;
    this.pinnedArr[idx].value = pointX;
    this.pinnedArr[idx].id = pinId;

    // draw the pin plot line
    this.chart.xAxis[0].addPlotLine(this.pinnedArr[idx]);
    return pinId;
  }

  // an adapter method to emit 'pin' event to bound listeners
  // this event is expected to bubble up til monitor.page.controller is reached
  // if these widgets want to respond to that event.
  // then it will notify all widgets on a page with event data - giving them a chance to decide
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  showSelectedValue(pinDetails: any, xVal: number, seriesId: string, yVal: number): void {
    const event = {
      state: 'add',
      fromTime: xVal,
      toTime: xVal,
      pinDetails: pinDetails,
      yVal: yVal,
      originator: this.id
    };
    this.notifyPinEventListeners(event); //{event: event}
  }

  onPinnedIconClicked(pinName: string): void {
    const oldPinDetails = this.removeSelectedPin(pinName);
    const event = {
      state: 'remove',
      pinDetails: oldPinDetails,
      originator: this.id
    };
    this.notifyPinEventListeners(event); //{event: event}
  }

  // no events must be fired! just inner state management.
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  removeSelectedPin(pinName: string): any {
    const idx = parseInt(pinName.split('-')[1]);
    const timestamp = get(this.pinnedArr[idx - 1], 'value', null);
    this.chart.xAxis[0].removePlotLine('pin-' + idx);
    this.pinnedArr.splice(idx - 1, 1);
    if (this.pinnedArr.length > 0 && idx === 1) {
      const myVal = this.pinnedArr[0].value;
      this.chart.xAxis[0].removePlotLine('pin-2');
      this.pinnedArr.splice(0, 1);
      this.addPin(myVal);
    }
    return { pinNumber: idx, pinId: pinName, removedPinTimestamp: timestamp };
  }

  resetPinnedIcon(): void {
    this.removeSelectedPin('pin-2');
    this.removeSelectedPin('pin-1');
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  pickYAxisFormatter(): any {
    // default option of 'numeric-thousands'
    let axisUnitLabel = this.bitsToSize.bind(this);
    if (this.kpiName || this.kpiUnit) {
      const kpiDetails = this.kpiDetailsService.getKpiDetails();
      const kpiNameAndUnit: KPIDetails = kpiDetails.find(
        data => data.kpi === this.kpiName.split('_').pop()
      );
      const kpiUnitValue = this.kpiUnit ?? kpiNameAndUnit?.unit;
      if (this.yAxisTickLabelFormat && kpiUnitValue) {
        switch (this.yAxisTickLabelFormat) {
          case 'time':
            axisUnitLabel = value =>
              this.formatService.formatValueWithUnit(value, UNIT[kpiUnitValue]);

            break;
          case 'numeric':
            axisUnitLabel = value =>
              this.formatService.formatValueWithUnit(value, UNIT.bitsPerSecond);

            break;
          case 'percentage':
            axisUnitLabel = value => this.formatService.formatValueWithUnit(value, UNIT.percentage);

            break;
          case 'fixed':
            axisUnitLabel = this.fixedY_axixLabel.bind(this);

            break;
        }
      }
    }
    return axisUnitLabel;
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  get defaultOptions(): any {
    const mainColor = this.mainColor;
    const secColor = this.secColor;
    const axisUnitLabel = this.pickYAxisFormatter();

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    return {
      credits: {
        enabled: false
      },
      time: {
        useUTC: false
      },
      chart: {
        type: 'area',
        styledMode: true,
        zoomType: false,
        events: {
          selection: this.chartZoomWithTime
            ? this.onTimeSelection.bind(this)
            : this.zoomEventBubblesUp.bind(this),
          load: _loadChart,
          click: function (e) {
            const className = e.srcElement.className;
            if (typeof className === 'string' && className.includes('pin')) {
              that.onPinnedIconClicked(className);
            }
          }
        }
      },
      xAxis: {
        events: {
          setExtremes: this.syncExtremes
        }
      },
      yAxis: {
        title: {
          text: null,
          rotation: 0,
          align: 'high',
          offset: 0,
          y: -20,
          useHTML: true
        },
        labels: {
          //eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: function (this: any): string {
            return axisUnitLabel(this.value);
          }
        }
      },
      legend: {
        symbolHeight: 7,
        symbolWidth: 7,
        verticalAlign: 'top',
        align: 'left'
      },
      tooltip: {
        crosshair: false,
        style: {
          width: 300,
          whiteSpace: 'normal'
        },
        formatter: function () {
          const tooltipContent = [];
          if (this.point && !this.point.series?.tooltipOptions?.shared) {
            if (!this.x) {
              return '<span>Data not available</span>';
            } else {
              tooltipContent.push('<span style="font-size: 10px">');
              const pointRange = that.granularity ?? this.point.series.pointRange;
              tooltipContent.push(that.tooltipService.getTooltipDate(this.x, pointRange));
              tooltipContent.push('</span><br/>');
              tooltipContent.push('<span style="font-size: 12px">');
              tooltipContent.push(this.point.series.name);
              tooltipContent.push(': ');
              tooltipContent.push(this.point.y + `${that.conversionUnits[0]}`);
              tooltipContent.push('</span><br/>');

              this.point.additionalTooltip?.forEach(tooltip => {
                if (tooltip.color) {
                  tooltipContent.push('<span style="color: ' + tooltip.color + '">');
                  tooltipContent.push('● ');
                  tooltipContent.push('</span>');
                }
                tooltipContent.push(`<span style="font-size: 12px">${tooltip.label}: `);
                tooltipContent.push(tooltip.value);
                tooltipContent.push('</span><br/>');
              });
            }
          } else {
            if (!this.x || !this.points || !this.points[0] || !this.points[0].series) {
              return '<span>Data not available</span>';
            }
            tooltipContent.push('<span style="font-size: 10px">');
            const pointRange = that.granularity ?? this.points[0].series.basePointRange;
            tooltipContent.push(that.tooltipService.getTooltipDate(this.x, pointRange));
            tooltipContent.push('</span><br/>');

            if (typeof this.points[0].point.failureCodes === 'string' && this.points.length >= 1) {
              tooltipContent.push(
                `<span style='color:red;'>${that.getSymbolIcon(
                  this.points[0].point?.marker?.symbol
                )} </span>`
              );
              tooltipContent.push(this.points[0].point.failureCodes);
            } else {
              const additionalValue = [];
              if (this.points.length >= 1 && that.options.showTooltipTotal) {
                let totalValue = 0;
                this.points.forEach(value => {
                  if (value.series.state !== 'inactive') {
                    totalValue = totalValue + value.y;
                    return totalValue;
                  }
                });

                totalValue == 0 ? this.points[0].total : totalValue;

                tooltipContent.push('<span>Total: ');
                tooltipContent.push(axisUnitLabel(totalValue));
                tooltipContent.push('</span><br/>');
              }
              this.points.forEach(point => {
                const symbolIcon = that.getSymbolIcon(point?.series.symbol);
                if (point.series.visible && !point?.point?.isNull) {
                  tooltipContent.push('<span>');
                  if (point.color) {
                    tooltipContent.push(
                      `<span style='color:${point.color};'>${symbolIcon} </span>`
                    );
                  }
                  tooltipContent.push(point.series.name);
                  tooltipContent.push(': ');
                  tooltipContent.push(axisUnitLabel(point.y));
                  tooltipContent.push('</span><br/>');
                }
                point.point?.additionalTooltip?.forEach(tooltip => {
                  additionalValue.push(`<span style="font-size: 12px">${tooltip.label}: `);
                  additionalValue.push(tooltip.value);
                  additionalValue.push('</span><br/>');
                });
              });
              if (additionalValue.length) {
                tooltipContent.push(...additionalValue);
              }
            }
          }
          that.options.additionalTooltip?.forEach(tooltip => {
            tooltipContent.push(`<span>${tooltip.label}: `);
            tooltipContent.push(tooltip.value);
            tooltipContent.push('</span><br/>');
          });
          return tooltipContent.join('');
        },
        pointFormatter: function () {
          let totalText = '';
          if (that.options.showPinning) {
            return;
          }
          if (that.options.showTooltipTotal && this.series.index === 0) {
            totalText += `<br/>Total: ${axisUnitLabel(this.total, false)}`;
          }
          return (totalText += `<br/><span style='color:${this.color}'>\u25CF</span> ${
            this.series.name
          }: ${axisUnitLabel(this.y, false)}`);
        },
        positioner: function (labelWidth, _, point) {
          const plotX = point.plotX;
          const plotTop = this.chart.plotTop;
          let tooltipY = plotTop;
          const topMargin = this.chart.legend.options.verticalAlign === 'bottom' ? 10 : 20;
          if (this.chart.legend.display) {
            tooltipY = tooltipY - topMargin;
          }
          if (this.graphZoomed === 'false') {
            return {
              x: plotX - labelWidth > 0 ? plotX - 100 : plotX + 60,
              y: tooltipY
            };
          } else {
            const plotLeft = this.chart.plotLeft;
            const plotWidth = this.chart.plotWidth;
            labelWidth = labelWidth + 35;
            let tooltipX = plotX + plotLeft;
            if (plotX + labelWidth > plotLeft + plotWidth) {
              tooltipX = plotWidth - labelWidth - 10;
            }
            return {
              x: tooltipX,
              y: tooltipY
            };
          }
        }
      },
      plotOptions: {
        area: {
          turboThreshold: this.turboThreshold,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, mainColor],
              [1, secColor]
            ]
          },
          color: mainColor
        },
        series: {
          connectNulls: get(this.options, 'connectNulls', true),
          turboThreshold: this.turboThreshold,
          events: {
            unpinEvent: event => {
              this.onPinnedIconClicked(event.pinID);
            },
            pinEvent: eventParam => {
              this.showPinnedValue(eventParam[0], eventParam[1], eventParam[2]);
            }
          }
        }
      },
      series: this.getSeries(this.dataSource),
      exporting: {
        enabled: false
      }
    };
  }

  getSymbolIcon(symbolName: string): string {
    let symbol = '●';
    switch (symbolName) {
      case 'circle':
        symbol = '●';
        break;
      case 'diamond':
        symbol = '♦';
        break;
      case 'square':
        symbol = '■';
        break;
      case 'triangle':
        symbol = '▲';
        break;
      case 'triangle-down':
        symbol = '▼';
        break;
      case 'dash':
        symbol = '--';
        break;
    }
    return symbol;
  }

  //eslint-disable-next-line
  onTimeSelection(event: any): boolean {
    if (event.xAxis) {
      if (this.shouldSelect(event)) {
        this.datetimeExtremes.min =
          Math.ceil(event.xAxis[0].min / this.minTickInterval) * this.minTickInterval;
        this.datetimeExtremes.max =
          Math.floor(event.xAxis[0].max / this.minTickInterval) * this.minTickInterval +
          this.minTickInterval;
        this.timePicker.setFixedTimeRangeOnChartZoom(
          this.datetimeExtremes.min,
          this.datetimeExtremes.max,
          true
        );
      }
      return false;
    }
  }

  //eslint-disable-next-line
  shouldSelect(event: any): boolean {
    const min = event.xAxis[0].min;
    const max = event.xAxis[0].max;
    return max - min > this.zoomTickInterval;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getSeries(seriesDataSource: any): any {
    if (get(this.options, 'useNamedSerie', false)) {
      return map(seriesDataSource, item => this.getNamedSerie(item));
    } else {
      return map(seriesDataSource, item => this.getSerie(item));
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getSerie(dataSource: any): any {
    return {
      type: 'area',
      data: dataSource,
      marker: {
        enabled: false
      },
      name: 'Measured Value'
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getNamedSerie(dataSource: any): any {
    return {
      type: 'area',
      data: dataSource.data,
      color: get(dataSource, 'fillColor.stops[0][1]', undefined),
      fillColor: get(dataSource, 'fillColor', undefined),
      marker: {
        enabled: true
      },
      name: dataSource.name
    };
  }

  //Default input values
  get defaultChartOptions(): IChartOptions {
    return {
      title: {
        align: 'left',
        visible: false
      },
      subtitle: {
        align: 'left',
        visible: false
      },
      xAxis: {
        visible: true,
        grid: false,
        boundary: undefined, //unlimited
        type: 'linear'
      },
      yAxis: {
        visible: true,
        grid: false
      },
      series: []
    };
  }

  getChartOptions(): Highcharts.Options {
    const options = merge({}, this.defaultChartOptions, this.options);
    this.maxPinCount = get(this.options, 'maxPinCount', 2);
    const config: Highcharts.Options = {
      chart: {
        type: options.type
      },
      lang: {
        noData: 'No data found for selected time frame'
      },
      title: this.getTextProps(options.title),
      subtitle: this.getTextProps(options.subtitle),
      xAxis: this.getAxisProps(options.xAxis, 'xAxis'),
      yAxis: this.getAxisProps(options.yAxis, 'yAxis'),
      series: options.series
    };
    return merge({}, this.defaultOptions, config, get(this.options, 'override'));
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////
  //Accept partial data array values that will be appended to end of chart
  //points - Array [x, y]
  onUpdate(series: Array<Array<[number, number]>>): void {
    const boundary = get(this.options, 'xAxis.boundary', false);
    series.forEach((points, index) => {
      points.forEach(point => {
        const firstX = get(this.chart, 'series[index].data[0].x', 0);
        const shift = boundary && point[0] - firstX >= boundary;
        // const shift = boundary && this.chart.series[index].data.length >= boundary;
        this.chart.series[index].addPoint(point, true, shift);
      });
    });
  }

  // captures zoom related action that occurred in HiChart component, updates internal state
  // and bubbles up 'set/reset zoom' event
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  zoomEventBubblesUp(event: any): void {
    // we need to recognize the kind of zoom activity. There are 3 recognized actions:
    // reseting zoom; zooming in; zooming out.
    // Notice zooming out is possible on touch devices only (hicharts claim not supporting that on ordinary laptops)
    let zoomActivity = 'zoom-out';
    if (event.type === 'selection') {
      zoomActivity = 'zoom-in';
    }
    if (event.resetSelection) {
      zoomActivity = 'reset';
    }

    // prepare an object/event for bubbling up
    const zoomFiredEvent = {
      min: event.min || get(event, 'xAxis[0].min'),
      max: event.max || get(event, 'xAxis[0].max'),
      originator: this.id,
      state: zoomActivity
    };
    if (this.events) {
      this.events.next({ key: zoomActivity, props: zoomFiredEvent });
    }
    if (this.notifyZoomEventListeners) {
      this.notifyZoomEventListeners(zoomFiredEvent);
    }
  }

  /**
   * A listener for 'reset' notification, trigged by end user, clicking 'reset selection' button
   */
  resetZoomLevel(): void {
    if (this.chart) {
      // as we trigger zoom out event ourselves, we want the whole bubble up chain to be initiated
      this.chart.zoomOut();
    }
  }

  // a listener for zoom events, bubbled down from monitor page controller to individual widgets
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  zoomEventFired(event: any): void {
    if (event.originator === this.id) {
      return;
    }
  }

  //------------------------------------------------Chart Events--------------------------------------------------------

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  crosshairMovemouseEvent(chart: any, event: any, hoverPoint: any): any {
    const crosshairWidth = 1,
      margin = 10,
      nearestPoint = hoverPoint.plotX,
      x = nearestPoint + margin - crosshairWidth,
      y = event.pageY,
      path = ['M', x, chart.plotTop, 'L', x, chart.plotTop + chart.plotHeight];

    if (chart.crossLines) {
      // update lines
      chart.crossLines.attr({
        d: path
      });
    } else {
      // draw lines
      chart.crossLines = chart.renderer
        .path(path)
        .attr({
          'stroke-width': 0,
          stroke: blueColor,
          zIndex: 10
        })
        .add();
    }

    const formatedDate =
      '<div>' +
      this.dateFormatterService.getDate(hoverPoint.category) +
      '<br/>' +
      this.dateFormatterService.getTime(hoverPoint.category) +
      '</div>';

    if (chart.crossLabel && !this.disableCrosshairLabel()) {
      // update label
      chart.crossLabel.attr({
        x: x,
        y: 10 + (this.options.crosshairMargin || 0),
        text: formatedDate,
        class: 'crosshair-date'
      });
    } else if (!this.disableCrosshairLabel()) {
      // draw label
      chart.crossLabel = chart.renderer
        .text(chart.yAxis[0].toValue(y).toFixed(2), chart.plotLeft - margin, y + 6)
        .add();
    }
  }

  //Sync Zoom
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  syncExtremes(event: any): void {
    if (event.trigger !== 'syncExtremes') {
      // Prevent feedback loop
      Highcharts.charts.forEach(chart => {
        if (chart && chart !== this.chart) {
          const seriesVisible = chart.series?.some(serie => serie.visible);
          // check if owner of chart has disabled synchronized zooming
          if (get(chart.options, 'disableZoom') || !seriesVisible) {
            // eg, this is the case with view.traceroute.latency component
            return;
          }
          if (get(chart, 'xAxis[0].setExtremes')) {
            // It is null while updating
            chart.xAxis[0].setExtremes(event.min, event.max, undefined, false, {
              trigger: 'syncExtremes'
            });
          }
        }
      });
    }
  }

  onMouseLeave(): void {
    Highcharts.charts.forEach(chart => {
      if (chart) {
        if (chart.pointer) {
          chart.pointer.reset(null, null);
        }

        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        const crossLabel = (chart as any).crossLabel;
        if (crossLabel && !this.disableCrosshairLabel()) {
          crossLabel.attr({
            text: ''
          });
        }

        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tooltip = (chart as any).tooltip;
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        const pinElement = (chart as any).pinElement;

        if (tooltip) {
          tooltip.hide();
        }

        if (pinElement) {
          pinElement.hide();
        }
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  onMouseMove(chart: any, event: any): void {
    // Hide mouse over event for legends

    if (
      (chart?.legend.display && event.offsetY < chart.legend.legendHeight + 10) ||
      !this.options.override.tooltip.shared
    ) {
      return;
    }
    const timeStamp = get(chart, 'hoverPoint.category');

    //Sync all charts tooltips
    Highcharts.charts?.forEach(chartObj => {
      if (chartObj && timeStamp) {
        //chartObj in this case is in fact an HTMLDOMElement however angular doesn't like how Highcharts has declared that reference
        const seriesVisible = chartObj?.series.some(serie => serie.visible);
        //eslint-disable-next-line
        if (!(<any>chartObj).renderTo || !seriesVisible) {
          //traceroute hops chart or availability chart
          return;
        }
        const e = chartObj.pointer.normalize(event); // Find coordinates within the chart

        event.chartX = e.offsetX;

        const points = this._getPointsByTimeStamp(chartObj, timeStamp);
        if (points) {
          let validSeries = true;
          points.forEach(point => {
            validSeries = validSeries === false ? false : !!point?.series;
          });
          if (validSeries) {
            try {
              chartObj.tooltip.refresh(points); // Show the tooltip
            } catch (error) {
              //silence highcharts errors
            }
          }

          points.forEach(point => {
            point?.setState('normal');

            if (point) {
              // if (this._showCrosshair()) {
              //   this.crosshairMovemouseEvent(chartObj, event, point);
              // }
              //traceroute chart
              //eslint-disable-next-line @typescript-eslint/no-explicit-any
              if ((<any>chartObj).series[0].name === 'Latency' || this.options.isPinApplicable) {
                this._repaintPinnerFor(chartObj, point, chart);
              }
            }
          });
        }
      }
    });

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    Highcharts.Pointer.prototype.reset = function () {};
  }

  /**
   * NOTE: do not assume this is the same chart object, as is currently bound to current view.threshold controller!
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  _repaintPinnerFor(chartObj: any, hoverPoint: any, paintEventOriginatorChart: any): void {
    if (!chartObj.pinElement || !this.options.showPinning) {
      return; //nothing to do
    }

    if (!this._allowMorePins()) {
      chartObj.pinElement.hide();
      return;
    }

    const pixelLocation = chartObj.xAxis[0].toPixels(hoverPoint.x, false) - 10; // 10 pix - approx half the width of pinner element
    const pinLocation = chartObj.chartHeight > 120 ? 40 : 10;
    chartObj.pinElement.attr('transform', 'translate(' + pixelLocation + ',' + pinLocation + ')');
    const pinStart =
      chartObj.chartHeight > 120
        ? get(chartObj.xAxis[0], 'userOptions.labels.enabled') === false
          ? 65
          : 77
        : 45;
    chartObj.pinLeg.attr('d', 'M 10 20 L 10 ' + (chartObj.chartHeight - pinStart));

    if (paintEventOriginatorChart === chartObj) {
      // click event to be defined on chart, where mouse is hovered right now. but not in others
      let seriesId = hoverPoint.series.options.id;
      if (!seriesId) {
        seriesId = this.id;
      }
      // it is important to register click listener on head only. Otherwise, in case of zoom, hichart generates two events:
      // one for 'zoom', another for 'onclick'. Which is plain wrong.
      chartObj.pinHead.attr(
        'onclick',
        `window.thresholdPins.pinTooltip('${this.id}',[${hoverPoint.x},'${seriesId}',${hoverPoint.y}])`
      );
    } else {
      // clearing click listener
      chartObj.pinHead.attr('onclick', '');
    }
    chartObj.pinElement.show();
  }

  _showCrosshair(): boolean {
    return !this.options.showPinning;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  _getPointsByTimeStamp(chart: any, timeStamp: any): Highcharts.Point[] {
    const points = [];
    chart.series.forEach(serie => {
      points.push(...serie.points);
    });
    return points.filter(
      point =>
        !point?.isNull && Math.round(point.category / 60000) === Math.round(timeStamp / 60000)
    );
  }

  // this is an event listener to listen for 'pin' events, bubbled down from monitor.page.controller to individual widgets
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  pinEventFired(event: any) {
    if (!this.options.showPinning) {
      return;
    }
    if (event.originator === this.id) {
      return;
    }
    const firstPin = some(this.pinnedArr, pin => pin.fromTime === event.fromTime);
    if (event.state === 'add' && !firstPin) {
      this.timeout.set(
        'pinEventListener',
        () => {
          this.addPin(event.fromTime);
        },
        0
      );
    } else if (event.state === 'remove') {
      this.timeout.set(
        'pinEventListener',
        () => {
          this.removeSelectedPin(event.pinDetails.pinId);
        },
        0
      );
    } else if (event.state === 'replace' || firstPin) {
      // only handle UI changes, no event emitting!
      this.timeout.set(
        'pinEventListener',
        () => {
          this.removeSelectedPin(event.oldPinDetails.pinId);
          this.addPin(event.fromTime);
        },
        0
      );
    } else if (event.state === 'clear-all') {
      this.resetPinnedIcon();
    } else if (event.state === 'repinLast') {
      this.rePinLastSelectedValue(event);
    }
  }

  // Clear all selected pin and pin only selected value
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  rePinLastSelectedValue(event: any): void {
    // clear all pins whaterver we had in the chart
    this.resetPinnedIcon();
    this.pinnedArr = [];

    //pin only selected items
    const pinDetails = this.addPin(event.fromTime);
    if (pinDetails) {
      this.showSelectedValue(pinDetails, event.fromTime, null, null);
    }
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  disableCrosshairLabel(): any {
    return get(this.options, 'disableTopCrosshairLabel', true);
  }

  private getTextProps(props: ITextType): ITextType {
    if (props.visible === false) {
      props.text = undefined;
    }
    return props;
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getAxisProps(props: IAxis, prefix: string): any {
    const useGrid = props.grid;

    if (useGrid) {
      this.classes.push(prefix + '-grid');
    }

    const axisOptions = {
      visible: props.visible,
      lineWidth: useGrid ? 1 : 0,
      gridLineWidth: useGrid ? 1 : 0,
      minorGridLineWidth: useGrid ? 1 : 0,
      labels: props.labels,
      title: props.title,
      type: props.type
    };
    if (!axisOptions.labels) {
      // recent change in minor version of highcharts now requires this option to be defined, even if I have nothing for it.
      axisOptions.labels = {};
    }
    return axisOptions;
  }
}

function _loadChart() {
  this.pinElement = this.renderer.g('pinElement').attr({ position: 'relative', zIndex: 100 }).add();
  this.pinHead = this.renderer
    .g('pinHead')
    .attr({
      transform: 'scale(0.8)',
      cursor: 'pointer'
    })
    .add(this.pinElement);
  this.renderer.circle(13, 13, 15).attr({ fill: '#fff', 'fill-opacity': 0 }).add(this.pinHead);
  this.renderer
    .path([
      'M25.917,11.567C25.203,5.062,19.695,0,13,0C5.82,0,0,5.82,0,13c0,6.893,5.368,12.517,12.149,12.957' +
        'C12.432,25.975,12.713,26,13,26s0.568-0.025,0.851-0.043c6.304-0.409,11.383-5.298,12.066-11.524C25.969,13.962,26,13.485,26,13' +
        'S25.969,12.038,25.917,11.567z M13,24c-3.052,0-5.817-1.252-7.812-3.267l6.527-4.999l2.358,2.358' +
        'c1.55-1.551,1.834-3.172,1.537-4.844l0.015-0.012l4.928-3.758c0.404-0.324,0.344-0.761,0.053-1.052l-3.171-3.168' +
        'c-0.269-0.27-0.658-0.35-0.988,0.053l-3.888,4.947c-1.657-0.268-3.246,0.016-4.775,1.545l2.358,2.358l-4.997,6.524' +
        'C3.203,18.7,2,15.989,2,13C2,6.935,6.935,2,13,2s11,4.935,11,11C24,19.065,19.065,24,13,24z'
    ])
    .attr({ fill: '#030104', 'stroke-width': 1, stroke: 'black' })
    .add(this.pinHead);
  this.pinLeg = this.renderer
    .path(['M 10 0 L 10 100'])
    .attr({
      fill: '#030104',
      'stroke-width': 2,
      stroke: 'black'
    }) // no cursor:pointer on leg -> only pin head is clickable!
    .add(this.pinElement);
  this.pinElement.hide();
}
